import React, { useRef } from 'react';
import { CONSOLE_HEADER_SECTION_ELEMENT_ID } from 'src/commons/constants';
import { useIsMobile } from '@amzn/storm-ui';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import {
  useLayoutResizeObserver,
  usePageHeaderLayout,
} from 'src/commons/hooks';
import { ConnectedConsoleLevelBannerWithAces } from './components/Banners/Common/BannerWithAces';
import MobileBreadcrumb from './components/BreadcrumbIA2/MobileBreadcrumb';
import { ConnectedPageHeader } from './components/PageHeader/PageHeader';
import SkipNavCard from './components/SkipNavCard/SkipNavCard';
import MobileSpoofingInfo from './components/SpoofingInfo/MobileSpoofingInfo';
import {
  GlobalStyle,
  MarketplaceSwitcherWrapper,
  StickyMobileHeader,
} from './styles';
import { ConsoleHeaderContent, ToastBannerWrapper } from './UtilityComponents';
import { MobileAccountSwitcherSection } from './MobileAccountSwitcherSection';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { MobileAccountSwitcherOverlay } from './MobileAccountSwitcherOverlay';
import { AccountDropdownProvider } from './components/AccountDropdown/AccountDropdownProvider';
import MarketplaceSwitcher from './components/MarketplaceSwitcher/MarketplaceSwitcher';
import { resolveSwitcherVisibility } from './switcherVisibility';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { isStandardPageLayoutType } from 'src/commons/utils/helpers';

export const MobileHeader: React.VFC<{
  pageLayoutType: PAGE_LAYOUT_TYPE;
  isSpoofing: boolean;
}> = ({ pageLayoutType, isSpoofing }) => {
  const chromeConfig = useChromeConfigContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isInterstitialPage } = internalChromeConfig;
  const isMobile = useIsMobile();
  const isStandardPage = isStandardPageLayoutType(pageLayoutType);
  const shouldShowAccountDropdown = !isInterstitialPage && isStandardPage;

  const consoleHeaderSectionRef = useRef<HTMLDivElement>(null);
  const pageHeaderSection = useRef<HTMLDivElement>(null);
  const breadcrumbs = chromeConfig?.breadcrumbs || [];
  const breadcrumb =
    breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : null;
  const marketplaceSwitcher = chromeConfig?.marketplaceSwitcher;
  const isFocusMode =
    pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS ||
    pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;
  // V2350387302: this gate used to be hand-rolled here, duplicating the
  // "marketplace config omitted means on" default and the focus-mode rule that
  // the shared resolver already owns. Deriving it from the resolver keeps
  // mobile from drifting away from the desktop and Trim headers.
  //
  // Mobile web deliberately does NOT host the region switcher, so
  // `regionSwitcherEnabled` is pinned false rather than read from the config:
  // on a region-switcher page mobile keeps showing the marketplace switcher,
  // which is the pre-existing behaviour. Rendering the region switcher here
  // would put an unverified third-party control into a narrow viewport, so it
  // needs its own change with a mobile visual check and VR baselines. When that
  // happens, pass the real flag through and render it from `hasRegionSwitcher`
  // rather than adding a second hand-rolled gate.
  const { hasMarketplaceSwitcher } = resolveSwitcherVisibility({
    marketplaceEnabled:
      marketplaceSwitcher === undefined ||
      !!marketplaceSwitcher.useMarketplaceSwitcher,
    marketplaceExplicitlyEnabled: !!marketplaceSwitcher?.useMarketplaceSwitcher,
    regionSwitcherEnabled: false,
    pageLayoutType,
  });
  const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] =
    React.useState(false);
  useLayoutResizeObserver({ consoleHeaderSectionRef });
  usePageHeaderLayout(pageHeaderSection);

  const handleAccountSwitcherClose = () => {
    AccountSwitcherPublisher.publishCounterMonitor('Close', 1);
    setIsAccountSwitcherOpen(false);
  };

  return (
    <>
      <GlobalStyle
        shouldShiftAppWrapper={false}
        isInterstitialPage={isInterstitialPage}
        isMobile={isMobile}
      />
      <SkipNavCard />
      <StickyMobileHeader
        id={CONSOLE_HEADER_SECTION_ELEMENT_ID}
        ref={consoleHeaderSectionRef}
      >
        <ConnectedConsoleLevelBannerWithAces />
        {isSpoofing && <MobileSpoofingInfo />}
        <ConsoleHeaderContent pageLayoutType={pageLayoutType} isMobile={true} />
      </StickyMobileHeader>
      <div ref={pageHeaderSection}>
        {!isFocusMode && (
          <>
            {shouldShowAccountDropdown && (
              <AccountDropdownProvider>
                <MobileAccountSwitcherSection
                  handleToggle={() => setIsAccountSwitcherOpen(true)}
                />
                <MobileAccountSwitcherOverlay
                  isOpen={isAccountSwitcherOpen}
                  handleClose={handleAccountSwitcherClose}
                />
              </AccountDropdownProvider>
            )}
            {breadcrumb && <MobileBreadcrumb breadcrumb={breadcrumb} />}
            {hasMarketplaceSwitcher && (
              <MarketplaceSwitcherWrapper data-ccx-e2e-id="aac-marketplace-switcher-wrapper">
                <MarketplaceSwitcher
                  pathOverride={marketplaceSwitcher?.marketplacePath}
                />
              </MarketplaceSwitcherWrapper>
            )}
          </>
        )}
        <ConnectedPageHeader />
      </div>
      <ToastBannerWrapper isMobile={true} />
    </>
  );
};
